import { useEffect, useMemo, useState } from 'react';
import { Wallet, LogOut, ExternalLink, AlertCircle, ArrowRightLeft, ChevronDown, PartyPopper } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAuthStore } from '@/stores/auth-store';
import { useAppStore } from '@/stores/app-store';
import { useRealtimeCredits } from '@/hooks/use-realtime-credits';
import { useAdminDetails } from '@/hooks/use-admin-details';
import { LICENSING_EXCLUDED_APPS } from '@/config/constants';
import { ReviewCreditsClaim } from '@/components/common/review-credits-claim';
import { CreditLoanOffer } from '@/components/common/credit-loan-offer';

/**
 * Credit threshold below which the whole app is blocked. Users are allowed to
 * run slightly negative for billing tolerance; once they dip past this cushion
 * we stop them from loading more usage.
 */
const CREDIT_FLOOR = -25;
const STORE_URL = 'https://sms.ulgebra.com/store?product=prod_MGbKLzmgcTnb40';

/**
 * Full-screen blocker shown when the billing user is out of credits.
 *
 * Billing user resolution:
 *  - If the current user is using shared-admin access
 *    (`selectedAdminUserId` set), the admin's credit balance is authoritative —
 *    the shared user inherits admin's billing.
 *  - Otherwise the current user's own credits are used.
 *  - Impersonation is deliberately NOT considered: the admin is still the one
 *    being billed while they debug, so we check the admin's own credits.
 */
export function CreditsBlockerDialog() {
  const user = useAuthStore((s) => s.user);
  const selectedAdminUserId = useAuthStore((s) => s.selectedAdminUserId);
  const selectedAdminResolved = useAuthStore((s) => s.selectedAdminResolved);
  const invitedAdminList = useAuthStore((s) => s.invitedAdminList);
  const fetchInvitedAdminList = useAuthStore((s) => s.fetchInvitedAdminList);
  const setSelectedAdmin = useAuthStore((s) => s.setSelectedAdmin);
  const signOut = useAuthStore((s) => s.signOut);

  const appCodeTPA = useAppStore((s) => s.appCodeTPA);

  const usingSharedAdmin = !!selectedAdminUserId;
  const billingUid = selectedAdminUserId || user?.uid || null;
  const credits = useRealtimeCredits(billingUid);

  // Apps in LICENSING_EXCLUDED_APPS are free or separately billed — they
  // don't consume Ulgebra credits, so the out-of-credits blocker must never
  // gate them (mirrors the trial/subscription exclusion in useTrial).
  const isExcludedApp = LICENSING_EXCLUDED_APPS.has(appCodeTPA || '');

  const admin = useAdminDetails(); // cached + CF-backed admin lookup

  const [scrollEl, setScrollEl] = useState<HTMLDivElement | null>(null);
  const [canScrollDown, setCanScrollDown] = useState(false);
  const [loanJustTaken, setLoanJustTaken] = useState(false);
  const [loanSuccess, setLoanSuccess] = useState<number | null>(null);
  const [holdBalance, setHoldBalance] = useState<number | null>(null);
  const [adminWaitTimedOut, setAdminWaitTimedOut] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setAdminWaitTimedOut(true), 5000);
    return () => clearTimeout(timer);
  }, []);

  const loanAddedAmount = loanJustTaken && holdBalance !== null && credits !== null && credits > holdBalance
    ? Math.round((credits - holdBalance) * 100) / 100
    : null;
  const shownLoanAmount = loanSuccess ?? loanAddedAmount;
  const showLoanSuccess = shownLoanAmount !== null;

  useEffect(() => {
    if (!scrollEl) return;
    const update = () => setCanScrollDown(scrollEl.scrollHeight - scrollEl.scrollTop - scrollEl.clientHeight > 8);
    scrollEl.addEventListener('scroll', update);
    const observer = new ResizeObserver(update);
    observer.observe(scrollEl);
    Array.from(scrollEl.children).forEach((child) => observer.observe(child));
    return () => {
      scrollEl.removeEventListener('scroll', update);
      observer.disconnect();
    };
  }, [scrollEl]);

  // Lazy-load the invited admin list so the switch-admin UI can render.
  // Cheap (one Firestore read, cached in-memory after first call).
  const extensionName = useAppStore((s) => s.extensionName);
  useEffect(() => {
    if (invitedAdminList === null && extensionName && user?.email) {
      fetchInvitedAdminList().catch(() => {});
    }
  }, [invitedAdminList, fetchInvitedAdminList, extensionName, user?.email]);

  // Filter out the currently-selected admin (or the current user when on
  // own account — the list helper already excludes self entries, but
  // selectedAdminUserId is the only one we explicitly skip here).
  const otherAdmins = useMemo(() => {
    if (!invitedAdminList) return [];
    return Object.values(invitedAdminList).filter(
      (a) => a.invitedAdminUserID !== selectedAdminUserId,
    );
  }, [invitedAdminList, selectedAdminUserId]);

  // Wait until we have a number before deciding — null means still loading.
  if (!user || credits === null) return null;
  if (!selectedAdminResolved && !adminWaitTimedOut) return null;
  if (isExcludedApp) return null;
  if (credits >= CREDIT_FLOOR && !loanJustTaken) return null;

  const displayEmail = user.email || 'Signed in';
  const adminName = admin?.name || '';
  const adminEmail = admin?.email || '';
  const adminPic = admin?.photoURL;
  // While the CF lookup is in flight, show the UID so the user has something
  // to reference if they need to ping their admin before the name resolves.
  const adminDisplay = adminName || adminEmail || selectedAdminUserId || 'your admin';
  const adminLabel = adminName || adminEmail || 'your admin';

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center px-4 pt-16 pb-4"
      role="dialog"
      aria-modal="true"
    >
      {/* Frosted backdrop — lets the user glimpse the app behind but blocks interaction */}
      <div className="absolute inset-0 bg-background/70 backdrop-blur-md animate-fade-in" />

      {/* Top-right email + logout chip */}
      <div className="absolute top-4 right-4 flex items-center gap-2 rounded-full border border-border/60 bg-card/80 backdrop-blur-md px-3 py-1.5 shadow-sm">
        <div className="flex items-center gap-2 min-w-0">
          {user.photoURL ? (
            <img src={user.photoURL} alt="" className="h-6 w-6 rounded-full shrink-0" />
          ) : (
            <div className="h-6 w-6 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
              <span className="text-[10px] font-semibold text-primary">
                {displayEmail[0]?.toUpperCase()}
              </span>
            </div>
          )}
          <span className="text-xs font-medium text-foreground truncate max-w-[180px]">
            {displayEmail}
          </span>
        </div>
        <button
          onClick={() => signOut()}
          className="flex items-center gap-1 rounded-full px-2 py-1 text-xs text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
          title="Sign out"
        >
          <LogOut className="h-3.5 w-3.5" />
          Sign out
        </button>
      </div>

      {showLoanSuccess ? (
        <div className="relative w-full max-w-sm rounded-2xl border border-border bg-card shadow-2xl p-8 text-center space-y-5 animate-fade-in-up">
          <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-emerald-500/15">
            <PartyPopper className="h-7 w-7 text-emerald-600 dark:text-emerald-400" />
          </div>
          <div>
            <h2 className="text-xl font-semibold text-foreground">Credits added!</h2>
            <p className="text-sm text-muted-foreground mt-1">
              {shownLoanAmount} credits have been added to your account. They will be taken back from your next renewal.
            </p>
          </div>
          <Button
            className="w-full"
            size="lg"
            onClick={() => {
              setLoanSuccess(null);
              setHoldBalance(null);
              setLoanJustTaken(false);
            }}
          >
            Continue to app
          </Button>
        </div>
      ) : (
      <div className="relative w-full max-w-md lg:w-auto lg:max-w-none rounded-2xl border border-border bg-card/95 backdrop-blur-xl shadow-2xl max-h-full overflow-y-auto animate-fade-in-up" style={{ animationDuration: "0.35s" }} ref={setScrollEl}>
        <div className="flex flex-col lg:flex-row">
        <div className="p-5 space-y-4 lg:p-8 lg:space-y-6 lg:w-[28rem] flex flex-col">
          <div className="flex flex-col items-center text-center space-y-2 lg:space-y-3">
            <div className="flex h-11 w-11 lg:h-14 lg:w-14 items-center justify-center rounded-2xl bg-gradient-to-br from-rose-500/15 to-amber-500/15">
              <Wallet className="h-5 w-5 lg:h-7 lg:w-7 text-rose-600 dark:text-rose-400" />
            </div>
            <div>
              <h2 className="text-lg lg:text-xl font-semibold text-foreground">Out of credits</h2>
              <p className="text-sm text-muted-foreground mt-1">
                You can't access the application without enough credits.
              </p>
            </div>
          </div>

          <div className="rounded-xl border border-border/60 bg-muted/40 p-3 lg:p-4 text-center">
            <p className="text-xs uppercase tracking-wider text-muted-foreground">
              Current balance
            </p>
            <p className={`mt-1 text-2xl lg:text-3xl font-bold tabular-nums ${credits >= CREDIT_FLOOR ? 'text-emerald-600 dark:text-emerald-400' : 'text-rose-600 dark:text-rose-400'}`}>
              {credits}
            </p>
            {usingSharedAdmin && (
              <p className="text-[11px] text-muted-foreground mt-1">
                From {adminLabel}'s account
              </p>
            )}
          </div>

          {usingSharedAdmin ? (
            <div className="rounded-lg border border-amber-400/40 bg-amber-50 dark:bg-amber-500/10 p-4 text-sm space-y-3">
              <div className="flex gap-2">
                <AlertCircle className="h-4 w-4 text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium text-amber-800 dark:text-amber-200">
                    Your admin needs to buy credits
                  </p>
                  <p className="text-xs text-amber-800/80 dark:text-amber-200/80 mt-1">
                    Please ask <span className="font-medium">{adminDisplay}</span> to top up Ulgebra credits to continue using the app.
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-3 rounded-md border border-amber-400/30 bg-white/60 dark:bg-amber-950/30 px-3 py-2">
                {adminPic ? (
                  <img src={adminPic} alt="" className="h-8 w-8 rounded-full shrink-0" />
                ) : (
                  <div className="h-8 w-8 rounded-full bg-amber-500/20 flex items-center justify-center shrink-0">
                    <span className="text-xs font-semibold text-amber-800 dark:text-amber-200">
                      {(adminDisplay || '?')[0]?.toUpperCase()}
                    </span>
                  </div>
                )}
                <div className="min-w-0 flex-1">
                  <p className="text-xs font-medium text-amber-900 dark:text-amber-100 truncate">
                    {adminName || (admin?.loading ? selectedAdminUserId : adminEmail || selectedAdminUserId)}
                  </p>
                  {adminEmail && adminEmail !== adminName && (
                    <p className="text-[11px] text-amber-800/80 dark:text-amber-200/80 truncate">
                      {adminEmail}
                    </p>
                  )}
                  {admin?.loading && !adminName && (
                    <p className="text-[10px] text-amber-700/70 dark:text-amber-300/60 mt-0.5">
                      Loading admin details…
                    </p>
                  )}
                </div>
              </div>
            </div>
          ) : (
            <Button
              className="w-full mt-auto hidden lg:inline-flex"
              size="lg"
              onClick={() => window.open(STORE_URL, '_blank', 'noopener,noreferrer')}
            >
              <ExternalLink className="h-4 w-4" />
              Buy credits
            </Button>
          )}

          {/* Switch organization: shown whenever the user has at least one
              OTHER admin (= organization) to fall back on. Picking another
              calls setSelectedAdmin which reloads the page; the dialog
              then re-evaluates against the new organization's credits and
              dismisses if they have enough. Works in both branches above —
              shared-org (switch to a different organization) AND
              own-account (switch to a shared organization with credits).
              Each `admin` in invitedAdminList represents an organization
              the user has been invited into; we render them here as
              user-facing organizations even though the data model still
              keys them by the admin's UID. */}
          {otherAdmins.length > 0 && (
            <div className="rounded-lg border border-border bg-muted/20 p-4 space-y-3">
              <div className="flex items-center gap-2">
                <ArrowRightLeft className="h-4 w-4 text-muted-foreground" />
                <p className="text-xs font-medium text-foreground">
                  Switch organization
                </p>
              </div>
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {otherAdmins.map((a) => {
                  const display = a.invitedAdminUserName || a.invitedAdminUserEmail || a.invitedAdminUserID;
                  const showEmailLine = a.invitedAdminUserEmail && a.invitedAdminUserEmail !== a.invitedAdminUserName;
                  return (
                    <button
                      key={a.invitedAdminUserID}
                      onClick={() => {
                        setSelectedAdmin(a.invitedAdminUserID, a.invitedAdminUserEmail || null).catch(() => {});
                      }}
                      className="w-full flex items-center gap-3 rounded-md border border-border bg-card hover:bg-muted px-3 py-2 transition-colors text-left"
                    >
                      {a.invitedAdminUserPic ? (
                        <img src={a.invitedAdminUserPic} alt="" className="h-8 w-8 rounded-full shrink-0" />
                      ) : (
                        <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                          <span className="text-xs font-semibold text-primary">
                            {(display || '?')[0]?.toUpperCase()}
                          </span>
                        </div>
                      )}
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium text-foreground truncate">{display}</p>
                        {showEmailLine && (
                          <p className="text-xs text-muted-foreground truncate">{a.invitedAdminUserEmail}</p>
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        <div className="hidden has-[>div]:flex lg:w-80 flex-col gap-3 border-t lg:border-t-0 lg:border-l border-border bg-muted/30 p-4 lg:p-6 animate-fade-in" style={{ animationDelay: "0.15s" }}>
          <p className="text-sm font-semibold text-foreground">Need credits right now?</p>
          <ReviewCreditsClaim />
          {!usingSharedAdmin && (
            <CreditLoanOffer
              onHold={() => {
                setHoldBalance(credits);
                setLoanJustTaken(true);
              }}
              onRelease={() => {
                setHoldBalance(null);
                setLoanJustTaken(false);
              }}
              onSuccess={setLoanSuccess}
            />
          )}
        </div>
        </div>

        {!usingSharedAdmin && (
          <div className="lg:hidden border-t border-border p-4">
            <Button
              className="w-full"
              size="lg"
              onClick={() => window.open(STORE_URL, '_blank', 'noopener,noreferrer')}
            >
              <ExternalLink className="h-4 w-4" />
              Buy credits
            </Button>
          </div>
        )}

        {canScrollDown && (
          <div className="sticky bottom-3 h-0 flex justify-center">
            <button
              onClick={() => scrollEl?.scrollTo({ top: scrollEl.scrollHeight, behavior: 'smooth' })}
              className="-translate-y-full flex h-9 w-9 items-center justify-center rounded-full border border-border bg-card shadow-lg text-muted-foreground hover:text-foreground animate-fade-in"
              title="Scroll down"
            >
              <ChevronDown className="h-4 w-4" />
            </button>
          </div>
        )}
      </div>
      )}
    </div>
  );
}
